// thrux-scroll-reveal.js — Web Animations API-driven reveals.
// Elements opt in via `data-reveal`. CSS hides them by default (when html.js
// class is set); JS uses element.animate() to fade them in, then sets
// `data-revealed` so they persist visible after the animation completes.
//
// Hero reveals fire shortly after DOM ready. Bridge + stats reveal when
// scrolled into view via IntersectionObserver.
//
// prefers-reduced-motion → CSS keeps elements visible (opacity:1) and JS
// skips animate calls, just stamps data-revealed.

(function () {
  // Mark JS-active synchronously — runs as part of script execution, before
  // first paint of body content (defer scripts run after parse, before DCL).
  document.documentElement.classList.add('js');

  var reduceMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function reveal(el, delayMs) {
    // Sets [data-revealed] after delayMs. CSS rule
    // `html.js [data-reveal][data-revealed]` flips opacity 0 → 1 with the
    // 0.5s transition. If the browser's animation timeline is throttled
    // (automation tab, background tab, low-power), the throttle detector
    // below adds `html.thrux-no-anim` which snaps elements visible without
    // transition — page renders correctly even when animations stall.
    if (el.hasAttribute('data-revealed')) return;
    setTimeout(function () { el.setAttribute('data-revealed', ''); }, delayMs || 0);
  }

  // Throttle detector — if document.timeline.currentTime hasn't advanced
  // after 100ms, the tab isn't ticking animations. Add a class so CSS
  // bypasses transitions and snaps reveals straight to visible state.
  // Caveat: Chrome automation tabs and Safari background tabs report
  // currentTime=0 indefinitely; this is the only reliable detection.
  (function detectThrottle() {
    if (!document.timeline) {
      document.documentElement.classList.add('thrux-no-anim');
      return;
    }
    var t1 = document.timeline.currentTime;
    setTimeout(function () {
      var t2 = document.timeline.currentTime;
      if (t2 === t1 || t2 === null || typeof t2 !== 'number') {
        document.documentElement.classList.add('thrux-no-anim');
      }
    }, 100);
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    // Hero reveals — cascade name → hammer with built-in delays.
    var heroName = document.querySelector('.athlete-hero__name[data-reveal]');
    var heroHammer = document.querySelector('.athlete-hero__hammer[data-reveal]');
    if (heroName) reveal(heroName, 50);
    if (heroHammer) reveal(heroHammer, 200);

    // Bridge cascade: lockup → eyebrow → headline → body line.
    var bridgeDelays = [
      ['.athlete-brand__lockup[data-reveal]', 50],
      ['.athlete-brand .athlete-section-number[data-reveal]', 150],
      ['.athlete-brand__headline[data-reveal]', 250],
      ['.athlete-brand__body-line[data-reveal]', 500]
    ];

    // Strike draw-in: when bridge is revealed, also tag the section so the
    // strikethrough CSS rule fires its draw-in animation.
    function revealBridge() {
      bridgeDelays.forEach(function (pair) {
        var el = document.querySelector(pair[0]);
        if (el) reveal(el, pair[1]);
      });
      var brandSection = document.querySelector('.athlete-brand');
      if (brandSection) brandSection.setAttribute('data-revealed', '');
    }

    // Stats cascade: staggered 80ms apart per card.
    function revealStats() {
      var cards = document.querySelectorAll('.athlete-stat-card[data-reveal]');
      cards.forEach(function (el, i) { reveal(el, i * 80); });
    }

    // IntersectionObserver fires reveal on first scroll-into-view.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var section = entry.target;
        if (section.classList.contains('athlete-brand')) revealBridge();
        if (section.classList.contains('athlete-stats')) revealStats();
        io.unobserve(section);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    var brand = document.querySelector('.athlete-brand');
    var stats = document.querySelector('.athlete-stats');
    if (brand) io.observe(brand);
    if (stats) io.observe(stats);

    // Science article + ingredients pages — generic per-section reveal. Each
    // `.science-article-section` / `.ingredients-page-section` (and the hero)
    // gets its own observer; on intersection, every `[data-reveal]` inside
    // reveals with an 80ms stagger.
    var articleSections = document.querySelectorAll('.science-article__hero, .science-article-section, .ingredients-page__hero, .ingredients-page-section, .ingredients-cta');
    if (articleSections.length) {
      var articleIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var children = entry.target.querySelectorAll('[data-reveal]');
          children.forEach(function (el, i) { reveal(el, i * 80); });
          articleIo.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

      articleSections.forEach(function (sec) { articleIo.observe(sec); });
    }

    // Sticky CTA hides when Final CTA section enters viewport — avoids two
    // yellow conversion buttons stacking when the user is already at the
    // closing CTA. Tag the sticky with a "suppressed" class; CSS slides it
    // out using the same hidden-state transform as the initial pre-hero state.
    var finalCta = document.querySelector('.athlete-final-cta');
    var stickyCta = document.querySelector('.athlete-sticky-cta');
    if (finalCta && stickyCta) {
      var finalIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            stickyCta.classList.add('athlete-sticky-cta--suppressed-by-final');
          } else {
            stickyCta.classList.remove('athlete-sticky-cta--suppressed-by-final');
          }
        });
      }, { threshold: 0.1 });
      finalIo.observe(finalCta);
    }

    // Product page — generic per-section reveal (same shape as science
    // articles). Each `.product-page__section` walks its [data-reveal]
    // children on intersection with an 80ms stagger.
    var productSections = document.querySelectorAll('.product-page__section');
    if (productSections.length) {
      var productIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var children = entry.target.querySelectorAll('[data-reveal]');
          children.forEach(function (el, i) { reveal(el, i * 80); });
          productIo.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
      productSections.forEach(function (sec) { productIo.observe(sec); });
    }

    // Product sticky CTA suppression — symmetric with athlete pattern, but
    // watches `.product-final-cta` and `.product-sticky-cta`.
    var productFinalCta = document.querySelector('.product-final-cta');
    var productStickyCta = document.querySelector('.product-sticky-cta');
    if (productFinalCta && productStickyCta) {
      var productFinalIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            productStickyCta.classList.add('product-sticky-cta--suppressed-by-final');
          } else {
            productStickyCta.classList.remove('product-sticky-cta--suppressed-by-final');
          }
        });
      }, { threshold: 0.1 });
      productFinalIo.observe(productFinalCta);
    }
  });
})();
